import { memo, useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  formatLastUpdated,
  getFreshnessInfo,
  getFreshnessDescription,
  type FreshnessLevel,
} from '@/utils/freshness';

const LEVEL_CLASSES: Record<FreshnessLevel, string> = {
  fresh: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  recent: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
  stale: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30',
  old: 'bg-muted text-muted-foreground border-border',
};

const DOT_CLASSES: Record<FreshnessLevel, string> = {
  fresh: 'bg-emerald-500',
  recent: 'bg-yellow-500',
  stale: 'bg-orange-500',
  old: 'bg-muted-foreground',
};

interface FreshnessIndicatorProps {
  /** Timestamp of the data being described */
  timestamp: string | number | Date | null | undefined;
  /** Prefix the label with "Updated" / "Actualizado" */
  prefix?: boolean;
  /** Compact inline text instead of a pill badge */
  variant?: 'badge' | 'inline';
  showIcon?: boolean;
  className?: string;
}

/**
 * Shows how current a piece of community data is, with a colour-coded
 * freshness level. Ticks itself every 30s so the label never goes stale.
 */
const FreshnessIndicator = memo(
  ({
    timestamp,
    prefix = false,
    variant = 'badge',
    showIcon = true,
    className,
  }: FreshnessIndicatorProps) => {
    const { language } = useLanguage();
    const [, setTick] = useState(0);

    useEffect(() => {
      const id = setInterval(() => setTick((t) => t + 1), 30000);
      return () => clearInterval(id);
    }, []);

    if (!timestamp) {
      return (
        <span className={cn('text-xs text-muted-foreground', className)}>
          {language === 'es' ? 'Sin datos aún' : 'No data yet'}
        </span>
      );
    }

    const info = getFreshnessInfo(timestamp, language);
    const text = prefix
      ? formatLastUpdated(timestamp, language)
      : info.label;
    const title = getFreshnessDescription(info.level, language);

    if (variant === 'inline') {
      return (
        <span
          title={title}
          className={cn('inline-flex items-center gap-1 text-xs text-muted-foreground', className)}
        >
          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', DOT_CLASSES[info.level])} />
          {showIcon && <Clock className="w-3 h-3 shrink-0" aria-hidden="true" />}
          <span>{text}</span>
        </span>
      );
    }

    return (
      <span
        title={title}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
          LEVEL_CLASSES[info.level],
          className
        )}
      >
        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', DOT_CLASSES[info.level])} />
        {showIcon && <Clock className="w-3 h-3 shrink-0" aria-hidden="true" />}
        <span>{text}</span>
      </span>
    );
  }
);

FreshnessIndicator.displayName = 'FreshnessIndicator';

export default FreshnessIndicator;
